import { PublishData, UpdateOneData, getDataWithFilterUrl, getOnedata } from './APIController';

/**
 * Task Management Controller
 * Handles all task-related operations including CRUD operations,
 * status management, and metric integration
 */

// Task status constants
export const TASK_STATUSES = {
  NOT_STARTED: 'not_started',
  ASSIGNED: 'assigned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  ON_HOLD: 'on_hold',
  REOPENED: 'reopened'
};



// Task priority constants
export const TASK_PRIORITIES = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// Frequency to task count mapping
const FREQUENCY_TASK_COUNT_MAP = {
  "Daily": 365,
  "Weekly": 52,
  "Monthly": 12,
  "Quarterly": 4,
  "Annually": 1,
  "Custom": 1
};



/**
 * Process workflow task approval and create next task if needed
 * @param {string} taskId - Task ID
 * @param {boolean} isApproved - Whether the task was approved
 * @param {string} comments - Approval comments
 * @returns {Promise<Object>} Updated task and next task if created
 */


/**
 * Create a new task
 * @param {Object} taskData - Task data object
 * @returns {Promise<Object>} Created task response
 */
export const createTask = async (taskData) => {
  try {
    // Validate required fields
    if (!taskData.Name || !taskData.StartDate || !taskData.EstimatedEndDate) {
      throw new Error('Missing required fields: Name, StartDate, EstimatedEndDate');
    }

    // Format relations for Strapi
    const formattedTaskData = {
      ...taskData,
      Status: taskData.Status || TASK_STATUSES.NOT_STARTED,
      Priority: taskData.Priority || TASK_PRIORITIES.MEDIUM,
      Progress: taskData.Progress || 0,
      CreationDate: new Date().toISOString(),
      LastUpdated: new Date().toISOString()
    };

    // Format metric field - ensure it's a string for Strapi
    if (taskData.metric) {
      formattedTaskData.metric = taskData.metric.toString();
    }

    // Format assignedTo field - ensure it's a string for Strapi
    if (taskData.assignedTo) {
      formattedTaskData.assignedTo = taskData.assignedTo.toString();
    }

    // Format Organization field - ensure it's a string for Strapi
    if (taskData.Organization) {
      formattedTaskData.Organization = taskData.Organization.toString();
    }

    // Format AssessmentYear field - ensure it's a string for Strapi
    if (taskData.AssessmentYear) {
      formattedTaskData.AssessmentYear = taskData.AssessmentYear.toString();
    }

    // Format createdBy relation (get from session storage)
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    if (userData?.user?.id) {
      formattedTaskData.createdBy = userData.user.id;
    }

    // Set default values
    const enhancedTaskData = {
      data: formattedTaskData
    };

         // Task data formatted for Strapi

    // Create the task
    const response = await PublishData(enhancedTaskData, "tasks");

    // Normalize created entity shape from middleware
    const createdEntity = response?.data ? response.data : response;
    const createdId = createdEntity?.id;



    // Fire-and-forget metric updates; do not fail task creation if these fail
    if (createdId && taskData.metric) {
      try {
        await updateMetricTaskCount(taskData.metric);
        await updateMetricComplianceStatus(taskData.metric);
      } catch (updateError) {
        // Metric updates failed - this is non-blocking for task creation
      }
    }

    return createdEntity;
  } catch (error) {
    console.error('Error creating task:', error);
    throw error;
  }
};

/**
 * Enhanced create task with additional validation and features
 * @param {Object} taskData - Task data object
 * @returns {Promise<Object>} Created task response
 */
export const createTaskEnhanced = async (taskData) => {
  try {
    // Enhanced validation
    if (!taskData.Name || !taskData.StartDate || !taskData.EstimatedEndDate || !taskData.metric) {
      throw new Error('Missing required fields: Name, StartDate, EstimatedEndDate, metric');
    }

    // Validate dates
    const startDate = new Date(taskData.StartDate);
    const endDate = new Date(taskData.EstimatedEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (startDate >= endDate) {
      throw new Error('Start date must be before end date');
    }
    
    if (startDate < today) {
      throw new Error('Start date cannot be in the past');
    }

    // Format relations for Strapi
    const formattedTaskData = {
      ...taskData,
      Status: taskData.Status || TASK_STATUSES.NOT_STARTED,
      Priority: taskData.Priority || TASK_PRIORITIES.MEDIUM,
      Progress: taskData.Progress || 0,
      CreationDate: new Date().toISOString(),
      LastUpdated: new Date().toISOString(),
      // Add complexity score
      ComplexityScore: calculateTaskComplexity(taskData),
      // Add estimated duration in days
      EstimatedDuration: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))
    };

    // Format metric field - ensure it's a string for Strapi
    if (taskData.metric) {
      formattedTaskData.metric = taskData.metric.toString();
    }

    // Format assignedTo field - ensure it's a string for Strapi
    if (taskData.assignedTo) {
      formattedTaskData.assignedTo = taskData.assignedTo.toString();
    }

    // Format Organization field - ensure it's a string for Strapi
    if (taskData.Organization) {
      formattedTaskData.Organization = taskData.Organization.toString();
    }

    // Format AssessmentYear field - ensure it's a string for Strapi
    if (taskData.AssessmentYear) {
      formattedTaskData.AssessmentYear = taskData.AssessmentYear.toString();
    }

    // Format createdBy relation (get from session storage)
    const userData = JSON.parse(sessionStorage.getItem("userData"));
    if (userData?.user?.id) {
      formattedTaskData.createdBy = userData.user.id;
    }

    // Set default values
    const enhancedTaskData = {
      data: formattedTaskData
    };

         // Enhanced task data formatted for Strapi

    // Create the task
    const response = await PublishData(enhancedTaskData, "tasks");

    // Normalize created entity shape from middleware
    const createdEntity = response?.data ? response.data : response;
    const createdId = createdEntity?.id;

         // Fire-and-forget metric updates; do not fail task creation if these fail
     if (createdId && taskData.metric) {
       try {
         await updateMetricTaskCount(taskData.metric);
         await updateMetricComplianceStatus(taskData.metric);
         
         // Task created successfully
       } catch (updateError) {
         // Metric updates failed - this is non-blocking for task creation
       }
     }

    return createdEntity;
  } catch (error) {
    console.error('Error creating enhanced task:', error);
    throw error;
  }
};

/**
 * Calculate task complexity based on various factors
 * @param {Object} taskData - Task data
 * @returns {number} Complexity score (1-10)
 */
const calculateTaskComplexity = (taskData) => {
  let score = 1;
  
  // Duration factor
  if (taskData.StartDate && taskData.EstimatedEndDate) {
    const startDate = new Date(taskData.StartDate);
    const endDate = new Date(taskData.EstimatedEndDate);
    const durationInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    
    if (durationInDays > 30) score += 2;
    else if (durationInDays > 7) score += 1;
  }
  
  // Priority factor
  if (taskData.Priority === TASK_PRIORITIES.CRITICAL) score += 2;
  else if (taskData.Priority === TASK_PRIORITIES.HIGH) score += 1;
  
  // Description complexity
  if (taskData.Description) {
    const wordCount = taskData.Description.split(' ').length;
    if (wordCount > 100) score += 2;
    else if (wordCount > 50) score += 1;
  }
  
  // Estimated hours factor
  if (taskData.EstimatedHours) {
    const hours = Number(taskData.EstimatedHours);
    if (hours > 40) score += 2;
    else if (hours > 8) score += 1;
  }
  
  return Math.min(10, score);
};

/**
 * Update task status and progress
 * @param {string} taskId - Task ID
 * @param {string} status - New status
 * @param {number} progress - Progress percentage (0-100)
 * @param {Object} additionalData - Additional data to update
 * @returns {Promise<Object>} Updated task response
 */
export const updateTaskStatus = async (taskId, status, progress = null, additionalData = {}) => {
  try {
    // Validate status transition
    const currentTask = await getOnedata("tasks", taskId);
    if (!currentTask?.data) {
      throw new Error('Task not found');
    }

    const currentStatus = currentTask.data.attributes.Status;
    const validTransitions = getValidStatusTransitions(currentStatus);
    
    if (!validTransitions.includes(status)) {
      throw new Error(`Invalid status transition from ${currentStatus} to ${status}`);
    }

    // Prepare update data
    const updateData = {
      Status: status,
      LastUpdated: new Date().toISOString(),
      ...additionalData
    };

    // Add progress if provided
    if (progress !== null) {
      updateData.Progress = Math.max(0, Math.min(100, progress));
    }

    // Update actual end date if completed
    if (status === TASK_STATUSES.COMPLETED && !currentTask.data.attributes.ActualEndDate) {
      updateData.ActualEndDate = new Date().toISOString();
    }

    // Update the task
    const response = await UpdateOneData("tasks", taskId, updateData);
    
    if (response?.data?.id) {
      // Update metric task count and compliance status
      const metricId = currentTask.data.attributes.metric?.data?.id;
      if (metricId) {
        await updateMetricTaskCount(metricId);
        await updateMetricComplianceStatus(metricId);
      }
    }

         return response;
   } catch (error) {
     // Silently handle task status update errors
     throw error;
   }
};

/**
 * Get tasks by metric ID
 * @param {string} metricId - Metric ID
 * @param {string} filters - Additional filters
 * @returns {Promise<Array>} Array of tasks
 */
export const getTasksByMetric = async (metricId, filters = "") => {
  try {
    const filterUrl = `metric[$eqi]=${metricId}${filters ? `&${filters}` : ""}`;
    const response = await getDataWithFilterUrl("tasks", filterUrl);
         return response?.data || [];
   } catch (error) {
     // Silently handle task fetching errors
     throw error;
   }
};

/**
 * Get tasks by assigned user
 * @param {string} userId - User ID
 * @param {string} filters - Additional filters
 * @returns {Promise<Array>} Array of tasks
 */
export const getTasksByUser = async (userId, filters = "") => {
  try {
    const filterUrl = `assignedTo[$eqi]=${userId}${filters ? `&${filters}` : ""}`;
    const response = await getDataWithFilterUrl("tasks", filterUrl);
         return response?.data || [];
   } catch (error) {
     // Silently handle task fetching errors
     throw error;
   }
};

/**
 * Get tasks by organization
 * @param {string} orgId - Organization ID
 * @param {string} filters - Additional filters
 * @returns {Promise<Array>} Array of tasks
 */
export const getTasksByOrganization = async (orgId, filters = "") => {
  try {
    const filterUrl = `Organization[$eqi]=${orgId}${filters ? `&${filters}` : ""}`;
    const response = await getDataWithFilterUrl("tasks", filterUrl);
         return response?.data || [];
   } catch (error) {
     // Silently handle task fetching errors
     throw error;
   }
};

/**
 * Update metric task count
 * @param {string} metricId - Metric ID
 * @returns {Promise<number>} Updated task count
 */
export const updateMetricTaskCount = async (metricId) => {
  try {
    const tasksFilter = `metric[$eqi]=${metricId}`;
    const tasks = await getDataWithFilterUrl("tasks", tasksFilter);
    const taskCount = tasks?.data?.length || 0;
    
    // Update metric with new task count
    await UpdateOneData("metrics", metricId, {
      TaskCount: taskCount,
      LastTaskUpdate: new Date().toISOString()
    });
    
         return taskCount;
   } catch (error) {
     // Silently handle metric update errors
     throw error;
   }
};

/**
 * Update metric compliance status based on task completion
 * @param {string} metricId - Metric ID
 * @returns {Promise<string>} Updated compliance status
 */
export const updateMetricComplianceStatus = async (metricId) => {
  try {
    // Get metric details
    const metric = await getOnedata("metrics", metricId);
    if (!metric?.data) {
      throw new Error('Metric not found');
    }

    const metricData = metric.data.attributes;
    const requiredCount = metricData.RequiredTaskCount || metricData.requiredCounter || 1;
    const currentCount = metricData.TaskCount || metricData.currentCounter || 0;
    
    // Calculate completion rate
    const completionRate = requiredCount > 0 ? (currentCount / requiredCount) * 100 : 0;
    
    // Determine compliance status
    let complianceStatus = 'pending';
    if (completionRate >= 100) {
      complianceStatus = 'compliant';
    } else if (completionRate >= 75) {
      complianceStatus = 'at_risk';
    } else if (completionRate > 0) {
      complianceStatus = 'non_compliant';
    }

    // Update metric
    await UpdateOneData("metrics", metricId, {
      TaskCompletionRate: completionRate,
      ComplianceStatus: complianceStatus,
      currentCounter: currentCount,
      requiredCounter: requiredCount
    });

         return complianceStatus;
   } catch (error) {
     // Silently handle metric update errors
     throw error;
   }
};

/**
 * Set required task count based on metric frequency
 * @param {string} metricId - Metric ID
 * @param {string} frequency - Frequency value
 * @returns {Promise<number>} Required task count
 */
export const setRequiredTaskCount = async (metricId, frequency) => {
  try {
    const requiredCount = FREQUENCY_TASK_COUNT_MAP[frequency] || 1;
    
    await UpdateOneData("metrics", metricId, {
      RequiredTaskCount: requiredCount,
      requiredCounter: requiredCount
    });

         return requiredCount;
   } catch (error) {
     // Silently handle required task count errors
     throw error;
   }
};

/**
 * Get valid status transitions for a task
 * @param {string} currentStatus - Current task status
 * @returns {Array} Array of valid next statuses
 */
export const getValidStatusTransitions = (currentStatus) => {
  const transitions = {
    [TASK_STATUSES.NOT_STARTED]: [TASK_STATUSES.ASSIGNED, TASK_STATUSES.ON_HOLD],
    [TASK_STATUSES.ASSIGNED]: [TASK_STATUSES.IN_PROGRESS, TASK_STATUSES.ON_HOLD],
    [TASK_STATUSES.IN_PROGRESS]: [TASK_STATUSES.COMPLETED, TASK_STATUSES.ON_HOLD],
    [TASK_STATUSES.COMPLETED]: [], // Final state
    [TASK_STATUSES.ON_HOLD]: [TASK_STATUSES.ASSIGNED, TASK_STATUSES.IN_PROGRESS]
  };
  
  return transitions[currentStatus] || [];
};

/**
 * Get task statistics for dashboard
 * @param {string} orgId - Organization ID
 * @returns {Promise<Object>} Task statistics
 */
export const getTaskStatistics = async (orgId) => {
  try {
    const tasks = await getTasksByOrganization(orgId);
    
    const stats = {
      total: tasks.length,
      byStatus: {},
      byPriority: {},
      byCategory: {},
      completionRate: 0,
      overdue: 0,
      dueThisWeek: 0
    };

    const now = new Date();
    const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    tasks.forEach(task => {
      const taskData = task.attributes;
      
      // Count by status
      stats.byStatus[taskData.Status] = (stats.byStatus[taskData.Status] || 0) + 1;
      
      // Count by priority
      stats.byPriority[taskData.Priority] = (stats.byPriority[taskData.Priority] || 0) + 1;
      
      // Check if overdue
      if (taskData.EstimatedEndDate && new Date(taskData.EstimatedEndDate) < now && taskData.Status !== TASK_STATUSES.COMPLETED) {
        stats.overdue++;
      }
      
      // Check if due this week
      if (taskData.EstimatedEndDate && new Date(taskData.EstimatedEndDate) <= oneWeekFromNow && taskData.Status !== TASK_STATUSES.COMPLETED) {
        stats.dueThisWeek++;
      }
    });

    // Calculate completion rate
    const completedTasks = stats.byStatus[TASK_STATUSES.COMPLETED] || 0;
    stats.completionRate = stats.total > 0 ? (completedTasks / stats.total) * 100 : 0;

         return stats;
   } catch (error) {
     // Silently handle task statistics errors
     throw error;
   }
};

/**
 * Bulk update task statuses
 * @param {Array} taskIds - Array of task IDs
 * @param {string} status - New status
 * @param {Object} additionalData - Additional data to update
 * @returns {Promise<Array>} Array of updated task responses
 */
export const bulkUpdateTaskStatus = async (taskIds, status, additionalData = {}) => {
  try {
    const results = [];
    
    for (const taskId of taskIds) {
      try {
        const result = await updateTaskStatus(taskId, status, null, additionalData);
        results.push({ taskId, success: true, result });
      } catch (error) {
        results.push({ taskId, success: false, error: error.message });
      }
    }

         return results;
   } catch (error) {
     // Silently handle bulk update errors
     throw error;
   }
};

/**
 * Delete a task
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteTask = async (taskId) => {
  try {
    // Get task details before deletion
    const task = await getOnedata("tasks", taskId);
    if (!task?.data) {
      throw new Error('Task not found');
    }

    const metricId = task.data.attributes.metric?.data?.id;
    
    // Delete the task (you'll need to implement this in APIController)
    // const response = await deleteData("tasks", taskId);
    
    if (metricId) {
      // Update metric task count and compliance status
      await updateMetricTaskCount(metricId);
      await updateMetricComplianceStatus(metricId);
    }

         return { success: true, message: 'Task deleted successfully' };
   } catch (error) {
     // Silently handle task deletion errors
     throw error;
   }
};

/**
 * Get task comments
 * @param {string} taskId - Task ID
 * @returns {Promise<Array>} Array of comments
 */
export const getTaskComments = async (taskId) => {
  try {
    const task = await getOnedata("tasks", taskId);
    if (!task?.data?.attributes?.Comments) {
      return [];
    }
    
         return Array.isArray(task.data.attributes.Comments) 
       ? task.data.attributes.Comments 
       : [task.data.attributes.Comments];
   } catch (error) {
     // Silently handle comment fetching errors
     throw error;
   }
};

/**
 * Add comment to task
 * @param {string} taskId - Task ID
 * @param {Object} comment - Comment object
 * @returns {Promise<Object>} Updated task response
 */
export const addTaskComment = async (taskId, comment) => {
  try {
    const task = await getOnedata("tasks", taskId);
    if (!task?.data) {
      throw new Error('Task not found');
    }

    const currentComments = task.data.attributes.Comments || [];
    const newComment = {
      id: Date.now().toString(),
      text: comment.text,
      author: comment.author,
      timestamp: new Date().toISOString(),
      ...comment
    };

    const updatedComments = Array.isArray(currentComments) 
      ? [...currentComments, newComment]
      : [newComment];

    const response = await UpdateOneData("tasks", taskId, {
      Comments: updatedComments,
      LastUpdated: new Date().toISOString()
    });

         return response;
   } catch (error) {
     // Silently handle comment addition errors
     throw error;
   }
};

/**
 * Get task dependencies
 * @param {string} taskId - Task ID
 * @returns {Promise<Array>} Array of dependent tasks
 */
export const getTaskDependencies = async (taskId) => {
  try {
    const task = await getOnedata("tasks", taskId);
    if (!task?.data?.attributes?.Dependencies) {
      return [];
    }
    
    const dependencies = Array.isArray(task.data.attributes.Dependencies) 
      ? task.data.attributes.Dependencies 
      : [task.data.attributes.Dependencies];
    
    // Fetch dependency details
    const dependencyDetails = [];
    for (const depId of dependencies) {
      try {
        const depTask = await getOnedata("tasks", depId);
                 if (depTask?.data) {
           dependencyDetails.push(depTask.data);
         }
       } catch (error) {
         // Silently handle individual dependency fetch failures
       }
    }
    
         return dependencyDetails;
   } catch (error) {
     // Silently handle dependency fetching errors
     throw error;
   }
};

/**
 * Check if task can be started (dependencies completed)
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} Task readiness status
 */
export const checkTaskReadiness = async (taskId) => {
  try {
    const dependencies = await getTaskDependencies(taskId);
    const incompleteDeps = dependencies.filter(dep => 
      dep.attributes.Status !== TASK_STATUSES.COMPLETED
    );
    
    const isReady = incompleteDeps.length === 0;
    const blockingTasks = incompleteDeps.map(dep => ({
      id: dep.id,
      name: dep.attributes.Name,
      status: dep.attributes.Status
    }));
    
         return {
       isReady,
       blockingTasks,
       totalDependencies: dependencies.length,
       completedDependencies: dependencies.length - incompleteDeps.length
     };
   } catch (error) {
     // Silently handle task readiness errors
     throw error;
   }
};

/**
 * Get task timeline and milestones
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} Task timeline information
 */
export const getTaskTimeline = async (taskId) => {
  try {
    const task = await getOnedata("tasks", taskId);
    if (!task?.data) {
      throw new Error('Task not found');
    }
    
    const taskData = task.data.attributes;
    const startDate = new Date(taskData.StartDate);
    const endDate = new Date(taskData.EstimatedEndDate);
    const today = new Date();
    
    const timeline = {
      startDate: startDate,
      endDate: endDate,
      duration: Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)),
      daysElapsed: Math.ceil((today - startDate) / (1000 * 60 * 60 * 24)),
      daysRemaining: Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)),
      progress: taskData.Progress || 0,
      isOverdue: today > endDate && taskData.Status !== TASK_STATUSES.COMPLETED,
      isDueSoon: (endDate - today) / (1000 * 60 * 60 * 24) <= 7 && taskData.Status !== TASK_STATUSES.COMPLETED
    };
    
    // Calculate milestones
    const milestones = [];
    if (taskData.Progress >= 25) milestones.push({ name: '25% Complete', achieved: true, date: startDate });
    if (taskData.Progress >= 50) milestones.push({ name: '50% Complete', achieved: true, date: startDate });
    if (taskData.Progress >= 75) milestones.push({ name: '75% Complete', achieved: true, date: startDate });
    if (taskData.Progress >= 100) milestones.push({ name: 'Completed', achieved: true, date: new Date() });
    
    timeline.milestones = milestones;
    
         return timeline;
   } catch (error) {
     // Silently handle timeline errors
     throw error;
   }
};

/**
 * Get task performance metrics
 * @param {string} taskId - Task ID
 * @returns {Promise<Object>} Task performance data
 */
export const getTaskPerformance = async (taskId) => {
  try {
    const task = await getOnedata("tasks", taskId);
    if (!task?.data) {
      throw new Error('Task not found');
    }
    
    const taskData = task.data.attributes;
    const startDate = new Date(taskData.StartDate);
    const endDate = new Date(taskData.EstimatedEndDate);
    const today = new Date();
    
    // Calculate efficiency metrics
    const estimatedDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const actualDuration = taskData.Status === TASK_STATUSES.COMPLETED 
      ? Math.ceil((new Date(taskData.ActualEndDate || endDate) - startDate) / (1000 * 60 * 60 * 24))
      : Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
    
    const efficiency = estimatedDuration > 0 
      ? ((estimatedDuration - actualDuration) / estimatedDuration) * 100 
      : 0;
    
    const performance = {
      estimatedDuration,
      actualDuration,
      efficiency,
      isOnTrack: efficiency >= -10, // Within 10% of estimate
      isAhead: efficiency > 0,
      isBehind: efficiency < -10,
      progressRate: taskData.Progress / actualDuration, // Progress per day
      estimatedCompletion: taskData.Progress > 0 
        ? new Date(today.getTime() + ((100 - taskData.Progress) / (taskData.Progress / actualDuration)) * 24 * 60 * 60 * 1000)
        : null
    };
    
         return performance;
   } catch (error) {
     // Silently handle performance errors
     throw error;
   }
};
